import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, FileCheck2, FileText, Printer, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { money } from '../../lib/invoice';

const FILTERS = [
  { key: 'pendiente', label: 'Pendientes' },
  { key: 'facturada', label: 'Facturadas' },
  { key: 'all', label: 'Todas' },
];

const fmtDate = (d) => new Date(d).toLocaleString('es-MX', {
  timeZone: 'America/Mexico_City', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});

const copyInvoiceData = (o) => {
  const text = [
    `Pedido: #${o.id.slice(0, 8)}`,
    `Cliente: ${o.customer_name}`,
    `RFC: ${o.invoice_rfc || '-'}`,
    o.invoice_razon_social ? `Razón social: ${o.invoice_razon_social}` : null,
    `Subtotal (sin IVA): ${money(o.invoice_base)}`,
    `IVA 8%: ${money(o.invoice_iva)}`,
    o.invoice_isr > 0 ? `Retención ISR 1.25%: ${money(o.invoice_isr)}` : null,
    `Total: ${money(o.total)}`,
  ].filter(Boolean).join('\n');
  navigator.clipboard.writeText(text);
  toast.success('Datos copiados para facturar');
};

const Stat = ({ label, value, testId, accent }) => (
  <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
    <p className="text-gray-500 text-xs">{label}</p>
    <p className={`font-bold text-lg ${accent}`} data-testid={testId}>{value}</p>
  </div>
);

const InvoiceRow = ({ o, onToggle, onPrint }) => {
  const done = o.invoice_status === 'facturada';
  return (
    <tr className="border-t border-gray-800 text-sm" data-testid={`invoice-row-${o.id}`}>
      <td className="py-2 px-2 text-gray-400 whitespace-nowrap">{fmtDate(o.created_at)}</td>
      <td className="py-2 px-2 text-orange-400 font-mono">#{o.id.slice(0, 8)}</td>
      <td className="py-2 px-2 text-white">{o.customer_name}<div className="text-gray-500 text-xs">{o.invoice_razon_social || ''}</div></td>
      <td className="py-2 px-2 text-sky-300 font-mono">{o.invoice_rfc || <span className="text-red-400">Sin RFC</span>}</td>
      <td className="py-2 px-2 text-right text-gray-300">{money(o.invoice_base)}</td>
      <td className="py-2 px-2 text-right text-gray-300">{money(o.invoice_iva)}</td>
      <td className="py-2 px-2 text-right text-amber-400">{o.invoice_isr > 0 ? `-${money(o.invoice_isr)}` : '—'}</td>
      <td className="py-2 px-2 text-right text-white font-bold">{money(o.total)}</td>
      <td className="py-2 px-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === 'pagado' ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
          {o.payment_status === 'pagado' ? 'Cobrado' : 'Por cobrar'}
        </span>
      </td>
      <td className="py-2 px-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${done ? 'bg-sky-900/40 text-sky-300' : 'bg-yellow-900/40 text-yellow-400'}`} data-testid={`invoice-status-${o.id}`}>
          {done ? 'Facturada' : 'Pendiente'}
        </span>
      </td>
      <td className="py-2 px-2">
        <div className="flex gap-1 justify-end">
          <Button size="sm" variant="ghost" className="h-8 w-8 p-1 text-gray-300" onClick={() => copyInvoiceData(o)} title="Copiar datos" data-testid={`invoice-copy-${o.id}`}><Copy className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-1 text-gray-300" onClick={() => onPrint(o)} title="Imprimir ticket" data-testid={`invoice-print-${o.id}`}><Printer className="h-4 w-4" /></Button>
          <Button size="sm" className={`h-8 text-xs ${done ? 'bg-gray-700 hover:bg-gray-600' : 'bg-sky-600 hover:bg-sky-700'}`} onClick={() => onToggle(o)} data-testid={`invoice-toggle-status-${o.id}`}>
            {done ? <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reabrir</> : <><FileCheck2 className="h-3.5 w-3.5 mr-1" /> Facturada</>}
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const InvoicesTab = ({ backendUrl, onPrint }) => {
  const [filter, setFilter] = useState('pendiente');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(`${backendUrl}/api/invoices${qs}`);
      setList(res.ok ? await res.json() : []);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, filter]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (o) => {
    const status = o.invoice_status === 'facturada' ? 'pendiente' : 'facturada';
    const res = await fetch(`${backendUrl}/api/invoices/${o.id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (!res.ok) return toast.error('No se pudo actualizar');
    toast.success(status === 'facturada' ? 'Marcada como facturada' : 'Regresada a pendiente');
    load();
  };

  const sum = (k) => list.reduce((s, o) => s + (Number(o[k]) || 0), 0);

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-white text-xl font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-sky-400" /> Facturación</h2>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`invoice-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${filter === f.key ? 'bg-sky-600 border-sky-500 text-white' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
              {f.label}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={load} data-testid="invoice-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Solicitudes" value={list.length} testId="invoice-stat-count" accent="text-white" />
        <Stat label="Subtotal sin IVA" value={money(sum('invoice_base'))} testId="invoice-stat-base" accent="text-gray-200" />
        <Stat label="IVA 8%" value={money(sum('invoice_iva'))} testId="invoice-stat-iva" accent="text-sky-300" />
        <Stat label="ISR retenido" value={money(sum('invoice_isr'))} testId="invoice-stat-isr" accent="text-amber-400" />
        <Stat label="Total cobrado" value={money(sum('total'))} testId="invoice-stat-total" accent="text-green-400" />
      </div>
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-x-auto">
        {list.length === 0 ? (
          <p className="text-gray-500 text-center py-10" data-testid="invoice-empty">{loading ? 'Cargando...' : 'No hay solicitudes de factura'}</p>
        ) : (
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="text-gray-500 text-xs text-left">
                {['Fecha', 'Pedido', 'Cliente', 'RFC', 'Subtotal', 'IVA 8%', 'Ret. ISR', 'Total', 'Pago', 'Estado', ''].map((h, i) => (
                  <th key={h || i} className={`py-2 px-2 font-medium ${i >= 4 && i <= 7 ? 'text-right' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((o) => <InvoiceRow key={o.id} o={o} onToggle={toggleStatus} onPrint={onPrint} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
